import { cn } from '@/lib/utils';
import type { AbsenceType } from '../../types';
import { ABSENCE_COLOR_CLASSES } from '../../utils/absenceTypeColors';

interface LegendItem {
  color: string;
  label: string;
  pattern?: boolean;
  customStyle?: React.CSSProperties;
}

/** Statische Legend-Items (nicht abwesenheitsbezogen) */
const STATIC_ITEMS: LegendItem[] = [
  { color: 'bg-white border border-gray-200', label: 'Normal' },
  { color: 'bg-gray-100', label: 'Wochenende' },
  { color: 'bg-green-100', label: 'Arbeitstag' },
  { color: 'bg-white ring-2 ring-blue-400 ring-offset-2 border border-gray-200', label: 'Manager-Anmerkung' },
  {
    color: '',
    label: 'Geplant (vorgemerkt)',
    customStyle: {
      background:
        'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(168, 85, 247, 0.18) 3px, rgba(168, 85, 247, 0.18) 6px)',
      border: '1px dashed rgba(168, 85, 247, 0.55)',
    },
  },
];

/** Baut Legend-Items aus AbsenceTypes */
function absenceTypeToLegendItem(type: AbsenceType): LegendItem {
  const colorClass = ABSENCE_COLOR_CLASSES[type.color]?.bg ?? 'bg-gray-200';

  // Spezialfall: Betriebsferien mit Pattern
  if (type.systemKey === 'COMPANY_VACATION') {
    return { color: colorClass, label: type.name, pattern: true };
  }
  // Spezialfall: Feiertag
  if (type.systemKey === 'HOLIDAY') {
    return { color: colorClass, label: type.name };
  }
  // Urlaub: zusätzlich halber Urlaub anzeigen wenn allowHalfDay
  // (wird als separater Item hinzugefügt)
  return { color: colorClass, label: type.name };
}

interface LegendProps {
  absenceTypes?: AbsenceType[];
}

export function Legend({ absenceTypes = [] }: LegendProps) {
  // Abwesenheitsarten als Legend-Items
  const absenceItems: LegendItem[] = [];
  for (const type of absenceTypes) {
    absenceItems.push(absenceTypeToLegendItem(type));
    // Halber-Tag-Variante für urlaubswirksame Typen
    if (type.countsAsVacation && type.allowHalfDay && type.systemKey === 'VACATION') {
      const colorClass = ABSENCE_COLOR_CLASSES[type.color]?.bg ?? 'bg-blue-100';
      absenceItems.push({
        color: '',
        label: 'Halber Urlaub',
        customStyle: {
          background: `repeating-linear-gradient(135deg, ${colorClass === 'bg-blue-100' ? '#dbeafe' : '#e5e7eb'} 0px, ${colorClass === 'bg-blue-100' ? '#dbeafe' : '#e5e7eb'} 4px, white 4px, white 8px)`,
        },
      });
    }
  }

  const allItems = [...absenceItems, ...STATIC_ITEMS];

  return (
    <div className="flex flex-wrap items-center justify-center gap-4 p-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 rounded-lg">
      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Legende:</span>
      {allItems.map((item) => (
        <div key={item.label} className="flex items-center gap-2">
          <div
            className={cn('w-5 h-5 rounded', item.color)}
            style={
              item.customStyle
                ? item.customStyle
                : item.pattern
                  ? {
                      backgroundImage:
                        'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(255,255,255,0.4) 2px, rgba(255,255,255,0.4) 4px)',
                    }
                  : undefined
            }
          />
          <span className="text-xs text-gray-500 dark:text-gray-400">{item.label}</span>
        </div>
      ))}
      <span className="text-xs text-gray-500 dark:text-gray-400 italic">
        * = vorläufig berechnete Stunden
      </span>
    </div>
  );
}
